import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Plus, Loader2, Pencil, Trash2, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  getActionItems,
  saveActionItem,
  deleteActionItem,
  type ActionItemRow,
  type ActionItemPriority,
  type ActionItemStatus,
} from '@/services/db/actionItems';
import { useAuthStore } from '@/store/authStore';
import { FieldLabel, ChipGroup, type ChipOption } from './_shared';

interface Props {
  dsrId: string;
  isReadOnly: boolean;
}

const PRIORITY_OPTIONS: ChipOption<ActionItemPriority>[] = [
  { value: 'low',      label: 'Low',      color: '#3ba870' },
  { value: 'medium',   label: 'Medium',   color: '#00b4b4' },
  { value: 'high',     label: 'High',     color: '#d87820' },
  { value: 'critical', label: 'Critical', color: '#c0392b' },
];

const STATUS_OPTIONS: ChipOption<ActionItemStatus>[] = [
  { value: 'open',        label: 'Open' },
  { value: 'in_progress', label: 'In Progress', color: '#00b4b4' },
  { value: 'closed',      label: 'Closed',      color: '#3ba870' },
  { value: 'overdue',     label: 'Overdue',     color: '#c0392b' },
];

const PRIORITY_BADGE: Record<ActionItemPriority, string> = {
  low:      'bg-mozaic-green/15 text-mozaic-green',
  medium:   'bg-mozaic-orange/15 text-mozaic-orange',
  high:     'bg-mozaic-red/15 text-mozaic-red',
  critical: 'bg-mozaic-red/20 text-mozaic-red font-bold',
};

const STATUS_BADGE: Record<ActionItemStatus, string> = {
  open:        'bg-muted text-muted-foreground',
  in_progress: 'bg-mozaic-teal/15 text-mozaic-teal',
  closed:      'bg-mozaic-green/15 text-mozaic-green',
  overdue:     'bg-mozaic-red/15 text-mozaic-red',
};

interface EditState {
  id: string | null;
  description: string;
  assignedToName: string;
  dueDate: string;
  priority: ActionItemPriority;
  status: ActionItemStatus;
  resolutionNotes: string;
}

const EMPTY: EditState = {
  id: null,
  description: '',
  assignedToName: '',
  dueDate: '',
  priority: 'medium',
  status: 'open',
  resolutionNotes: '',
};

export default function ActionItemsForm({ dsrId, isReadOnly }: Props) {
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();

  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closedExpanded, setClosedExpanded] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['action_items', dsrId],
    queryFn: () => getActionItems(dsrId),
    staleTime: 30 * 1000,
  });

  const { open, closed } = useMemo(() => {
    const open: ActionItemRow[] = [];
    const closed: ActionItemRow[] = [];
    for (const i of items) {
      if (i.status === 'closed') closed.push(i);
      else open.push(i);
    }
    return { open, closed };
  }, [items]);

  const startAdd = () => {
    setError(null);
    setEdit({ ...EMPTY });
  };

  const startEdit = (item: ActionItemRow) => {
    setError(null);
    setEdit({
      id: item.id,
      description: item.description,
      assignedToName: item.assigned_to_name ?? '',
      dueDate: item.due_date ?? '',
      priority: item.priority,
      status: item.status,
      resolutionNotes: item.resolution_notes ?? '',
    });
  };

  const cancel = () => { setEdit(null); setError(null); };

  const save = async () => {
    if (!edit || !user || !tenantId) return;
    if (!edit.description.trim()) {
      setError('Description is required.'); return;
    }
    if (edit.status === 'closed' && !edit.resolutionNotes.trim()) {
      setError('Resolution notes are required when closing an item.'); return;
    }
    setSaving(true); setError(null);
    try {
      await saveActionItem({
        id: edit.id ?? undefined,
        dsrReportId: dsrId,
        tenantId,
        userId: user.id,
        description: edit.description.trim(),
        assignedToName: edit.assignedToName.trim() || null,
        dueDate: edit.dueDate || null,
        priority: edit.priority,
        status: edit.status,
        resolutionNotes: edit.resolutionNotes.trim() || null,
      });
      queryClient.invalidateQueries({ queryKey: ['action_items', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
      setEdit(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this action item?')) return;
    try {
      await deleteActionItem(id);
      queryClient.invalidateQueries({ queryKey: ['action_items', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const isOverdue = (item: ActionItemRow): boolean => {
    if (!item.due_date || item.status === 'closed') return false;
    return new Date(item.due_date) < new Date(new Date().toDateString());
  };

  const ItemCard = ({ item }: { item: ActionItemRow }) => (
    <div className="space-y-2 rounded-md border border-border bg-background p-3">
      <div className="flex items-start justify-between gap-2">
        <p className="flex-1 text-sm font-medium">{item.description}</p>
        <div className="flex shrink-0 gap-1">
          <Badge className={PRIORITY_BADGE[item.priority]}>{item.priority}</Badge>
          <Badge className={STATUS_BADGE[item.status]}>{item.status.replace('_', ' ')}</Badge>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {item.assigned_to_name && <span>→ {item.assigned_to_name}</span>}
        {item.due_date && (
          <span className={isOverdue(item) ? 'font-semibold text-mozaic-red' : ''}>
            Due {format(new Date(item.due_date), 'MMM d, yyyy')}
          </span>
        )}
        {item.source_section && <span>From: {item.source_section}</span>}
      </div>
      {item.resolution_notes && (
        <p className="text-xs text-muted-foreground">
          Resolution: {item.resolution_notes}
        </p>
      )}
      {!isReadOnly && (
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => startEdit(item)}>
            <Pencil className="mr-1 h-3 w-3" /> Edit
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-mozaic-red" onClick={() => remove(item.id)}>
            <Trash2 className="mr-1 h-3 w-3" /> Delete
          </Button>
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <h2 className="text-base font-semibold">Action Items</h2>
          <p className="text-xs text-muted-foreground">
            Follow-up tasks with assignees and due dates.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Open / In progress / Overdue */}
          {open.length > 0 && (
            <div className="space-y-2">
              {open.map((item) => <ItemCard key={item.id} item={item} />)}
            </div>
          )}

          {/* Closed group — collapsed by default */}
          {closed.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setClosedExpanded((x) => !x)}
                className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                {closedExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                Closed ({closed.length})
              </button>
              {closedExpanded && (
                <div className="mt-2 space-y-2">
                  {closed.map((item) => <ItemCard key={item.id} item={item} />)}
                </div>
              )}
            </div>
          )}

          {items.length === 0 && !edit && (
            <p className="rounded-md border border-dashed border-muted bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground">
              No action items yet.
            </p>
          )}

          {edit && (
            <div className="space-y-3 rounded-md border-2 border-mozaic-teal/40 bg-mozaic-teal/5 p-3">
              <div className="space-y-1">
                <FieldLabel required>Description</FieldLabel>
                <Textarea
                  rows={2}
                  value={edit.description}
                  onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                  placeholder="What needs to be done?"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <FieldLabel>Assigned To</FieldLabel>
                  <Input
                    value={edit.assignedToName}
                    onChange={(e) => setEdit({ ...edit, assignedToName: e.target.value })}
                    placeholder="e.g. J. Smith"
                  />
                </div>
                <div className="space-y-1">
                  <FieldLabel>Due Date</FieldLabel>
                  <Input
                    type="date"
                    value={edit.dueDate}
                    onChange={(e) => setEdit({ ...edit, dueDate: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <FieldLabel required>Priority</FieldLabel>
                <ChipGroup
                  options={PRIORITY_OPTIONS}
                  value={edit.priority}
                  onChange={(v) => v && setEdit({ ...edit, priority: v as ActionItemPriority })}
                  wrap
                />
              </div>
              <div className="space-y-1">
                <FieldLabel required>Status</FieldLabel>
                <ChipGroup
                  options={STATUS_OPTIONS}
                  value={edit.status}
                  onChange={(v) => v && setEdit({ ...edit, status: v as ActionItemStatus })}
                  wrap
                />
              </div>
              {edit.status === 'closed' && (
                <div className="space-y-1">
                  <FieldLabel required>Resolution Notes</FieldLabel>
                  <Textarea
                    rows={2}
                    value={edit.resolutionNotes}
                    onChange={(e) => setEdit({ ...edit, resolutionNotes: e.target.value })}
                    placeholder="How was this resolved?"
                  />
                </div>
              )}
              {error && <p className="text-xs text-mozaic-red">{error}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={cancel} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-mozaic-teal hover:bg-mozaic-teal/90"
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (edit.id ? 'Update Item' : 'Save Item')}
                </Button>
              </div>
            </div>
          )}

          {!isReadOnly && !edit && (
            <Button
              variant="outline"
              size="sm"
              className="border-dashed border-mozaic-teal text-mozaic-teal"
              onClick={startAdd}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add Action Item
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
